import React, { useContext, useEffect, useState } from "react";
import { Context } from "../../index";
import {Button, Card, Form, Modal, Pagination, Spinner, Table} from "react-bootstrap";
import { toast } from "react-toastify";
import {
    check,
    createSellVirtOrderApi,
    getSellVirtOrdersByUserIdApi,
    cancelSellVirtOrderApi,
    fetchTransferVirtToAnotherAccountApi
} from "../../http/userAPI";
import { observer } from "mobx-react-lite";

const SellVirtOnStock = ({ refreshKey = 0 }) => {
    const { user } = useContext(Context);
    const REVIEWS_QUALITY_MIN = 60;
// --- Sell guard based on reviewerData ---
    const rq = Number(user?.reviewerData?.reviewQuality100 ?? NaN);
    const complaints30 = Number(user?.reviewerData?.reviewerComplaintCount30Day ?? 0);
    const trust = Number(user?.user?.level_of_trust ?? user?.reviewerData?.level_of_trust ?? NaN);

    const isRqBad = Number.isFinite(rq) && rq < REVIEWS_QUALITY_MIN;
    const isComplaintsBad = complaints30 >= 3;
    const isTrustBad = !Number.isFinite(trust) || trust < 200;

    const sellBlocked = isRqBad || isComplaintsBad;

    const sellBlockReason = (() => {
        const reasons = [];
        if (isRqBad) reasons.push(`Reviews Quality ${Number.isFinite(rq) ? rq : "—"}% < ${REVIEWS_QUALITY_MIN}`);
        if (isComplaintsBad) reasons.push(`Complaints (30d) ${complaints30} ≥ 3`);
        if (isTrustBad) reasons.push(`Trust Level ${Number.isFinite(trust) ? trust : "—"} < 200`);
        return reasons.length ? `Selling is blocked: ${reasons.join(" and ")}.` : null;
    })();

    // Данные контекста продажи
    const [breakdown, setBreakdown] = useState(null); // { balance, pendingVirts, minReserve, formula }
    const [availableToSell, setAvailableToSell] = useState(0);
    const [loadingCtx, setLoadingCtx] = useState(true);

    // заявки пользователя + метрики рынка
    const [orders, setOrders] = useState([]);
    const [ordersCount, setOrdersCount] = useState(0);
    const [ordersPage, setOrdersPage] = useState(1);
    const [ordersLimit] = useState(25);
    const [marketAvg, setMarketAvg] = useState(null);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const ordersTotalPages = Math.max(1, Math.ceil(ordersCount / ordersLimit));
    const secondaryActivePageStyle = {
        backgroundColor: "#6c757d",
        borderColor: "#6c757d",
        color: "#fff",
    };

    const [showCancel, setShowCancel] = useState(false);
    const [orderToCancel, setOrderToCancel] = useState(null);
    const [cancelSubmitting, setCancelSubmitting] = useState(false);

    const openCancel = (order) => {
        setOrderToCancel(order);
        setShowCancel(true);
    };

    const doCancel = async () => {
        if (!orderToCancel) return;
        try {
            setCancelSubmitting(true);
            const res = await cancelSellVirtOrderApi({
                orderId: orderToCancel.id,
                userId: user?.user?.id,
                comment: "Canceled from frontend",
            });
            toast.success(res?.message || "Order canceled", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            setShowCancel(false);
            setOrderToCancel(null);
            await loadContext();           // обновить список и метрики
            const data = await check();    // обновить профиль/баланс
            user.setUser(data);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Failed to cancel order", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setCancelSubmitting(false);
        }
    };

// утилита форматирования дат
    const fmt = (d) => {
        if (!d) return "—";
        const date = new Date(d);
        const datePart = date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }); // "22 Aug 2025"
        const timePart = date.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        }); // "10:15"
        return `${datePart}, ${timePart}`;
    };

    // Поля формы
    const PRESETS = [1000, 3000, 5000, 10000, 20000, 30000, 40000, 50000, 75000, 100000];
    const [sellAmount, setSellAmount] = useState("");
    const [wantUsd, setWantUsd] = useState("");

    // Модал подтверждения SELL
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmInput, setConfirmInput] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const toNum = (v) => (v == null || v === "" ? 0 : Number(v));

    const loadContext = async () => {
        try {
            setLoadingCtx(true);
            setLoadingOrders(true);

            const dataTr = await fetchTransferVirtToAnotherAccountApi(user.user.id);
            const b = dataTr?.breakdown || {};
            const balance = toNum(b.balance);
            const pendingVirts = toNum(b.pendingVirts);
            const minReserve = toNum(b.minReserve ?? 1500);

            // что пришло с сервера
            let avail = toNum(dataTr?.availableTransferVirt);

            // на всякий случай, если сервер не прислал — посчитаем из breakdown
            if (!dataTr?.availableTransferVirt && dataTr?.breakdown) {
                avail = Math.max(balance - pendingVirts - minReserve, 0);
            }

            setBreakdown({ balance, pendingVirts, minReserve, formula: b.formula });
            setAvailableToSell(avail);

            // 2) заявки пользователя + средний рынок (последние 10 completed)
            const data = await getSellVirtOrdersByUserIdApi(user?.user?.id, {
                limit: ordersLimit,
                offset: (ordersPage - 1) * ordersLimit,
            });
            setOrders(data?.rows ?? []);
            setOrdersCount(Number(data?.count ?? 0));
            setMarketAvg(data?.marketAverage ?? null);

        } catch (e) {
            toast.error("Failed to load orders", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setLoadingCtx(false);
            setLoadingOrders(false);
        }
    };


    useEffect(() => {
        if (user?.user?.id) loadContext();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.user?.id, user?.user?.balance, refreshKey, ordersPage]);

    const overLimit = toNum(sellAmount) > toNum(availableToSell);
    const valid = !sellBlocked && toNum(sellAmount) >= 1000 && toNum(wantUsd) > 0 && !overLimit;

    const pricePerVirt = toNum(sellAmount) > 0 ? (toNum(wantUsd) / toNum(sellAmount)) : 0;
    const perThousand = pricePerVirt * 1000;

    const openConfirm = (e) => {
        e.preventDefault();
        if (!valid) {
            toast.warning("Check Virt amount, USD amount and limits.", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
            return;
        }
        setShowConfirm(true);
    };

    const handleSubmit = async () => {
        if (confirmInput.trim().toUpperCase() !== "SELL") return;

        try {
            setSubmitting(true);

            // вызов API
            const res = await createSellVirtOrderApi(
                user?.user?.id,
                Number(sellAmount),
                Number(wantUsd),
                Number(pricePerVirt.toFixed(6)),
                "Sell order from frontend" // можно передавать comment или null
            );

            toast.success(res?.message || "Sell Order Placed!", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });

            // Обновим контекст и профиль
            await loadContext();
            const data = await check();
            user.setUser(data);

            // Сброс формы
            setSellAmount("");
            setWantUsd("");
            setConfirmInput("");
            setShowConfirm(false);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Failed to place sell order", { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card className="p-3 w-100 mt-0">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
                <h5
                    className="text-muted mb-0 mb-md-0"
                    title={
                        sellBlocked
                            ? `${sellBlockReason} Selling is temporarily unavailable for your account.`
                            : "Sell Virt on the Exchange"
                    }
                >
                    Sell Virt on the Exchange
                    {sellBlocked && (
                        <span className="ms-2 badge bg-danger" role="alert">
      blocked
    </span>
                    )}
                </h5>

                {sellBlocked && (
                    <div className="alert alert-warning py-2 px-3 my-2 small" role="alert">
                        {sellBlockReason} Improve your review quality and reduce refund incidents to enable selling again.
                    </div>
                )}

                <small className="text-muted d-block">
                Here you can place a sell order to exchange your Virt for real funds.
                Specify the amount of Virt you want to sell and the price per unit.
                Your order will appear in the exchange order book and can be matched
                with buyers. Once the transaction is confirmed, the sold Virt will be
                deducted from your balance and the funds will be credited accordingly.
                <br />
                <div className="text-muted d-block mt-2">
                    <strong className="text-danger mt-2">Important:</strong> Only available Virt can be sold — pending Virt (linked to active assignments)
                    are excluded. A non-withdrawable minimum reserve of 1500 Virt is required
                    on your account. Orders remain active until fulfilled or canceled.
                </div>
                <div className="text-muted d-block mt-2 mb-3">
                    <strong className="text-danger me-1">Tip:</strong>
                    You can create multiple orders with different amounts and prices
                    to increase the chance of a successful sale.
                </div>
                <hr className="my-2" />

                {/* Available for Sale */}
                <div className="text-muted d-block mt-2 mb-3">
                    <strong className="text-danger me-1">Available amount for sale:</strong>
                    <strong className="me-2">{availableToSell} Virt</strong>
                    {breakdown && (
                        <span>
      (<span className="fst-italic">Calculated as:</span> Balance ({Number(breakdown.balance)} Virt) − Pending Virt ({Number(breakdown.pendingVirts)} Virt) − Min Reserve ({Number(breakdown.minReserve)} Virt) ={" "}
                            <strong>{Number(breakdown.balance) - Number(breakdown.pendingVirts) - Number(breakdown.minReserve)} Virt</strong>)
    </span>
                    )}
                    <div className="text-muted d-block mt-1 mb-1">
                        <strong>Reviews Quality:</strong>{" "}
                        {Number.isFinite(rq) ? `${rq}%` : "—"}{" "}
                        <span className={isRqBad ? "text-danger fw-bolder" : "text-success fw-semibold"}>
    {isRqBad ? `(You need at least ${REVIEWS_QUALITY_MIN}% positive reviews)` : "(OK)"}
  </span>
                        <br />
                        <strong>Complaints in last 30 days:</strong>{" "}
                        {complaints30}{" "}
                        <span className={isComplaintsBad ? "text-danger fw-bolder" : "text-success fw-semibold"}>
    {isComplaintsBad ? "(You can have no more than 2 complaints in 30 days)" : "(OK)"}
  </span>
                        <br />
                        <strong>Trust Level:</strong>{" "}
                        {Number.isFinite(trust) ? trust : "—"}{" points "}
                        <span className={isTrustBad ? "text-danger fw-bolder" : "text-success fw-semibold"}>
    {isTrustBad ? "(You need > 200 points to sell)" : "(OK)"}
  </span>
                        <br />
                        <span>
    <em>
      To sell Virt, your Reviews Quality must be {REVIEWS_QUALITY_MIN}% or higher, you must have fewer than 3 complaints
      in the last 30 days, and your Trust Level must be above 200.
    </em>
  </span>
                    </div>

                    <p> <strong className="text-danger me-1">Rate:</strong> <strong className="me-2">  Average rate of last 10 trades 1000 Virt ≈ ${marketAvg?.avg_usd_per_1000_virt?.toFixed(2)} </strong>
                </p>
                    </div>
            </small>




            {/* Bordered block: rate + inputs + button */}
            <div className="border border-secondary rounded w-100 p-3 mb-3">
                {/* Rate row with warning */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="small text-muted">
                        <strong className="text-danger fs-6">Rate:</strong>&nbsp;
                        {pricePerVirt > 0 ? (
                            <>
                                1 USD = {(1 / pricePerVirt).toFixed(2)} Virt
                                <span className="ms-2">| 1000 Virt ≈ ${perThousand.toFixed(2)}</span>

                            </>
                        ) : (
                            "—"
                        )}
                    </div>

                    {/* Warning message */}
                    {(toNum(sellAmount) < 1000 && toNum(sellAmount) > 0) || toNum(sellAmount) > availableToSell ? (
                        <strong className="text-danger fs-6">
                            Warning:&nbsp;
                            {toNum(sellAmount) < 1000
                                ? "Minimum is 1000 Virt"
                                : `Maximum is ${availableToSell} Virt`}
                        </strong>
                    ) : null}
                </div>


                {/* Responsive row: stack on mobile, single line on desktop */}
                <Form onSubmit={openConfirm}>
                    <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-end gap-2 gap-md-3">

                        {/* LEFT: presets + custom (inline) */}
                        <div className="flex-shrink-0" style={{ minWidth: 220 }}>
                            <Form.Group>
                                <Form.Label className="small text-muted mb-1">Amount of Virt to Sell (min 1000)</Form.Label>

                                {/* БЫЛО: align-items-stretch → СТАЛО: align-items-start */}
                                <div className="d-flex flex-column flex-md-row align-items-start gap-2">
                                    {/* Dropdown: фиксируем ширину и не даём растягиваться по высоте */}
                                    <div className="flex-shrink-0 align-self-start" style={{ width: 220 }}>
                                        <Form.Select
                                            size="sm"
                                            disabled={sellBlocked}
                                            value={
                                                PRESETS.includes(Number(sellAmount))
                                                    ? String(sellAmount)
                                                    : sellAmount
                                                        ? "custom"
                                                        : ""
                                            }
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (val === "custom") {
                                                    if (PRESETS.includes(Number(sellAmount))) setSellAmount("");
                                                } else {
                                                    setSellAmount(val);
                                                }
                                            }}
                                        >
                                            <option value="">Choose amount…</option>
                                            {PRESETS.map((v) => (
                                                <option key={v} value={v}>
                                                    {v.toLocaleString()} Virt
                                                </option>
                                            ))}
                                            <option value="custom">Enter custom amount…</option>
                                        </Form.Select>
                                    </div>

                                    {/* Справа появляется лейбл+инпут, не влияя на высоту селекта слева */}
                                    {(!PRESETS.includes(Number(sellAmount)) && !!sellAmount) ||
                                    (PRESETS.includes(Number(sellAmount)) ? false : sellAmount === "") ? (
                                        <Form.Group className="flex-shrink-0 align-self-start" style={{ width: 180 }}>

                                            <Form.Control
                                                size="sm"
                                                disabled={sellBlocked}
                                                type="number"
                                                min={100}
                                                step="100"
                                                value={sellAmount}
                                                onChange={(e) => setSellAmount(e.target.value)}
                                                onWheel={(e) => e.currentTarget.blur()}
                                                placeholder="Enter amount…"
                                                isInvalid={overLimit}
                                            />

                                        </Form.Group>
                                    ) : null}
                                </div>
                            </Form.Group>
                        </div>


                        {/* RIGHT: USD */}
                        <div style={{ width: 220 }} className="flex-shrink-0">
                            <Form.Group>
                                <Form.Label className="small text-muted mb-1">I want to receive (USD)</Form.Label>

                                <Form.Control
                                    size="sm"
                                    disabled={sellBlocked}
                                    type="number"
                                    min={1}
                                    step="0.01"
                                    value={wantUsd}
                                    onChange={(e) => setWantUsd(e.target.value)}
                                    onWheel={(e) => e.currentTarget.blur()}
                                    placeholder="Enter desired $"
                                />
                                {/* Пустая “прокладка” для выравнивания высоты с левым блоком на десктопе */}
                             </Form.Group>
                        </div>

                        {/* BUTTON: push to right on desktop */}
                        <div className="ms-md-auto d-flex justify-content-md-end">
                            <Button
                                type="submit"
                                size="sm"
                                className="btn-success align-self-end"
                                disabled={loadingCtx || !valid}
                            >
                                Place sell order
                            </Button>
                        </div>
                    </div>
                </Form>
            </div>


            {/* My sell orders */}
            <div className="mt-3 w-100">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-2">
                    <h6 className="mb-2 mb-md-0 text-success">Your Sell Orders ({ordersCount})</h6>

                    {/* Средний рынок по последним 10 completed */}
                    {marketAvg && marketAvg.trades_count > 0 && (
                        <h6 className="text-muted mb-0">
                            <span className="me-2">Average rate of last {marketAvg.trades_count} trades:</span>
                            <strong>1 USD = {marketAvg.avg_virt_per_usd} Virt</strong>
                            <span className="mx-2">|</span>
                            <strong>1000 Virt ≈ ${marketAvg.avg_usd_per_1000_virt}</strong>
                        </h6>
                    )}
                </div>

                <div style={{ overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: '8px' }}>
                    <Table bordered hover size="sm" striped className="mt-1" style={{ minWidth: '600px', lineHeight: '1.3' }}>
                        <thead className="table-light text-muted small">
                        <tr className="bg-secondary text-center bg-opacity-50 text-sm">
                            <th className="small text-muted">ID</th>
                            <th className="small text-muted">Created</th>
                            <th className="small text-muted">Status</th>
                            <th className="small text-muted">Virt</th>
                            <th className="small text-muted">USD</th>
                            <th className="small text-muted">1 USD = Virt</th>
                            <th className="small text-muted">1000 Virt ≈ $</th>
                            <th className="small text-muted">Purchased</th>
                            <th className="small text-muted">Buyer</th>
                            <th className="small text-muted text-center">Cancel</th>
                        </tr>
                        </thead>

                        <tbody className="small">
                        {loadingOrders ? (
                            <tr>
                                <td colSpan={10} className="text-center text-muted py-3">Loading orders…</td>
                            </tr>
                        ) : orders.length === 0 ? (
                            <tr>
                                <td colSpan={10} className="text-center text-muted py-3">No sell orders yet.</td>
                            </tr>
                        ) : (
                            orders.map((o) => (
                                <tr key={o.id}>
                                    <td className="text-center small align-middle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.id}</td>
                                    <td className="text-center small align-middle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{fmt(o.createdAt)}</td>
                                    <td className="text-center small align-middle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        <strong
                                            className={
                                                o.order_status === "available"
                                                    ? "text-success"
                                                    : o.order_status === "completed"
                                                        ? "text-primary"
                                                        : o.order_status === "canceled"
                                                            ? "text-secondary"
                                                            : "text-warning"
                                            }
                                            style={{ textTransform: "uppercase" }}
                                        >
                                            {o.order_status}
                                        </strong>
                                    </td>
                                    <td className="text-center small align-middle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {Number(o.virt_amount).toLocaleString()}</td>
                                    <td className="text-center small align-middle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        ${Number(o.usd_amount).toFixed(2)}</td>
                                    <td className="text-center small align-middle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {Number(o.price_virt_per_usd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td className="text-center small align-middle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        ${Number(o.price_usd_per_1000_virt).toFixed(2)}</td>
                                    <td className="text-center small align-middle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {fmt(o.purchased_at)}</td>
                                    <td className="text-center small align-middle" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>

                                    {o.buyer ? (o.buyer.amz_name || o.buyer.id) : "—"}
                                    </td>
                                    <td className="align-middle" style={{ whiteSpace: "nowrap" }}>
                                        <div className="d-flex justify-content-center">
                                            {o.order_status === "available" ? (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    className="p-0 d-flex align-items-center justify-content-center"
                                                    style={{ width: 18, height: 18, borderRadius: 3 }}
                                                    title="Cancel Order"
                                                    onClick={() => openCancel(o)}
                                                >
                                                    <strong style={{ position: "relative", top: "-1px" }}>×</strong>
                                                </Button>
                                            ) : (
                                                <span className="text-muted">—</span>
                                            )}
                                        </div>
                                    </td>

                                </tr>
                            ))
                        )}
                        </tbody>
                    </Table>
                </div>

                {ordersCount > ordersLimit && (
                    <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-2 mt-2">
                        <div className="small text-muted">
                            Showing {(ordersPage - 1) * ordersLimit + 1}-{Math.min(ordersPage * ordersLimit, ordersCount)} of {ordersCount} orders
                        </div>
                        <Pagination size="sm" className="mb-0">
                            <Pagination.First
                                disabled={ordersPage <= 1 || loadingOrders}
                                onClick={() => setOrdersPage(1)}
                            />
                            <Pagination.Prev
                                disabled={ordersPage <= 1 || loadingOrders}
                                onClick={() => setOrdersPage((prev) => Math.max(1, prev - 1))}
                            />
                            <Pagination.Item active linkStyle={secondaryActivePageStyle}>
                                {ordersPage} / {ordersTotalPages}
                            </Pagination.Item>
                            <Pagination.Next
                                disabled={ordersPage >= ordersTotalPages || loadingOrders}
                                onClick={() => setOrdersPage((prev) => Math.min(ordersTotalPages, prev + 1))}
                            />
                            <Pagination.Last
                                disabled={ordersPage >= ordersTotalPages || loadingOrders}
                                onClick={() => setOrdersPage(ordersTotalPages)}
                            />
                        </Pagination>
                    </div>
                )}
            </div>



            {/* SELL confirmation modal */}
            <Modal
                show={showConfirm}
                onHide={() => setShowConfirm(false)}
                dialogClassName="mt-2"          // pushes the dialog toward the top
                centered={false}                 // make sure it's not centered
            >
                <Modal.Header small closeButton style={{ height: 40 }}>
                    <Modal.Title className="fs-6">Confirm Sell Order</Modal.Title>
                </Modal.Header>

                <Modal.Body className="small">
                    <div className="mb-2 text-center">
                        <h6 className="text-muted mb-0">
                            You are about to place a sell order for{" "}
                            <strong>{Number(sellAmount).toLocaleString()} Virt</strong> for{" "}
                            <strong>${Number(wantUsd || 0).toFixed(2)}</strong>
                        </h6>

                        {pricePerVirt > 0 && (
                            <h6 className="text-muted mt-1 mb-0">
                                1 USD = {(1 / pricePerVirt).toFixed(2)} Virt &nbsp; | &nbsp;
                                1000 Virt ≈ ${perThousand.toFixed(2)}
                            </h6>
                        )}
                    </div>

                    <div className="text-muted mt-3">
                        <p className="mb-2">
                            If you confirm this order, the specified amount of <strong>Virt</strong> will be deducted from your balance immediately.
                            You will receive the corresponding <strong>USD</strong> on your main account once another user buys your lot.
                        </p>
                        <p className="mb-2">
                            You can cancel this order at any time.
                        </p>
                        <p className="mb-0">
                            Your current balance is <strong>{Number(breakdown?.balance || 0).toLocaleString()} Virt</strong>.
                        </p>
                        <p className="mb-0">
                            After placing this order, it will become{" "}
                            <strong>{(Number(breakdown?.balance || 0) - Number(sellAmount || 0)).toLocaleString()} Virt</strong>.
                        </p>
                        <hr />
                        <p className="mb-1 fs-6 text-center">
                            To confirm, type <strong className="text-danger">SELL</strong> below and click
                            <strong className="text-success"> “Place Order”</strong> button.
                        </p>

                    </div>


                    <Form.Control
                        className="mt-3 border border-secondary"
                        size="sm"
                        value={confirmInput}
                        onChange={(e) => setConfirmInput(e.target.value)}
                        placeholder="Type SELL"
                        autoFocus
                    />
                </Modal.Body>

                <Modal.Footer className="d-flex justify-content-between" style={{ height: 60 }}>
                    <Button variant="outline-secondary" size="sm" onClick={() => setShowConfirm(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="success"
                        size="sm"
                        disabled={confirmInput.trim().toUpperCase() !== "SELL" || submitting}
                        onClick={handleSubmit}
                    >
                        {submitting ? <Spinner animation="border" size="sm" /> : "  Place Order"}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showCancel}
                onHide={() => setShowCancel(false)}
                top
                size="sm"
            >
                <Modal.Header small closeButton style={{ height: 40 }}>
                    <Modal.Title className="fs-6">Cancel Sell Order</Modal.Title>
                </Modal.Header>

                <Modal.Body className="small text-center">
                    {orderToCancel ? (
                        <>
                            <h6 className="text-muted mb-2">
                                You are about to cancel order <strong>#{orderToCancel.id}</strong>.
                            </h6>
                            <h6 className="text-muted mb-2">
                                {Number(orderToCancel.virt_amount).toLocaleString()} Virt for ${Number(orderToCancel.usd_amount).toFixed(2)}
                            </h6>
                            <div className="text-muted">
                                1 USD = {Number(orderToCancel.price_virt_per_usd).toLocaleString(undefined,{ minimumFractionDigits: 2, maximumFractionDigits: 2 })} Virt
                                <span className="mx-2">|</span>
                                1000 Virt ≈ ${Number(orderToCancel.price_usd_per_1000_virt).toFixed(2)}
                            </div>
                            <hr />
                            <p className="mb-1 fs-6 text-center text-success fw-semibold">
                                Cancel order and refund {Number(orderToCancel.virt_amount).toLocaleString()} Virt?
                            </p>
                        </>
                    ) : (
                        <div className="text-muted">Loading…</div>
                    )}
                </Modal.Body>

                <Modal.Footer className="d-flex justify-content-between" style={{ height: 60 }}>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setShowCancel(false)}
                        disabled={cancelSubmitting}
                    >
                        No, Keep It
                    </Button>
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={doCancel}
                        disabled={cancelSubmitting}
                    >
                        {cancelSubmitting ? <Spinner animation="border" size="sm" /> : "Yes, Cancel"}
                    </Button>
                </Modal.Footer>
            </Modal>


        </div>
        </Card>
    );
};

export default observer(SellVirtOnStock);
